import React, { FC, useEffect, useState, useRef } from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import cx from 'classnames';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';
import {
  MAX_LENGTH_TRUNCATE_VALUE,
  AUTOCOMPLETE_PROPS,
} from '@/constant/global/global.constant';
import { APP_BG, APP_BORDER } from '@/constant/theme/colors.constant';
import { firstLetterCapitalise, getEllipsis } from '@/utils/generic';
import { tid } from '@/utils/testId';
import _ from 'lodash';
import { FiRefreshCcw } from 'react-icons/fi';
import AppButton from '@/components/atoms/AppButton';
import { Caption, Text } from '@/components';

interface List {
  id: string;
  [nameKey: string]: any;
}

interface AutoCompleteData {
  lists: any[];
  className?: string;
  label: string;
  placeholder?: string;
  selectedValue?: List | null | any;
  error?: boolean;
  readOnly?: boolean;
  nameKey?: string;
  multiSelect?: boolean;
  height?: string;
  disabled?: boolean;
  highlightField?: boolean;
  handleSelectedValue: (value: List | string) => void;
  handleSearch?: (value: any) => void;
  handleFetchMore?: () => void;
  isFetchingMore?: boolean;
  /** When true, list items come pre-filtered from the server — skip client-side re-filter */
  serverSideSearch?: boolean;
  isCapitalised?: boolean;
  dataTestId?: string;
  allowCustomValues?: boolean; // ✅ new optional prop
  isLargePadded?: boolean;
  ellipsisLength?: number;
  /** Hide the floating top label (default: false) */
  hideTopLabel?: boolean;
  /** Override the input border color class e.g. 'border-blue-500' (default: border-gray-600) */
  borderColor?: string;
  /** Called when the input loses focus */
  onBlur?: () => void;
  /** Accent text-color class used for label, chevron, header (default: 'text-primary') */
  accentColor?: string;
  /** Border class applied on focus (default: 'focus:border-primary') */
  focusBorderColor?: string;
  /** Tailwind gradient classes for the selected item background (default: 'from-primary via-secondary to-tertiary') */
  selectedGradient?: string;
  /** Background accent class for badges, icon wrappers, empty states (default: 'bg-primary/10 dark:bg-primary/15') */
  accentBg?: string;
  /** Min number of items required to enable search typing. Below this threshold the input is read-only (dropdown only). Default: 5 */
  /** When false, typing is disabled and clicking opens the dropdown. Default: true */
  enableSearch?: boolean;
}

const Autocomplete: FC<AutoCompleteData> = ({
  lists,
  label,
  handleSelectedValue,
  handleSearch = () => { },
  placeholder,
  selectedValue,
  readOnly,
  error,
  multiSelect,
  className,
  height,
  nameKey = 'name',
  highlightField,
  handleFetchMore,
  isFetchingMore = false,
  serverSideSearch = false,
  isCapitalised = false,
  dataTestId = '',
  allowCustomValues = false, // ✅ default false
  isLargePadded = false,
  ellipsisLength = MAX_LENGTH_TRUNCATE_VALUE,
  hideTopLabel = false,
  borderColor = 'border-gray-300 dark:border-gray-700',
  onBlur,
  accentColor = 'text-primary',
  focusBorderColor = 'border-primary',
  selectedGradient = 'from-primary via-secondary to-tertiary',
  accentBg = 'bg-primary/10 dark:bg-primary/15',
  enableSearch = true,
}) => {
  const [selected, setSelected] = useState<any>({});
  const [multiSelected, setMultiSelected] = useState<any[]>([]);
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const comboboxButtonRef = useRef<HTMLButtonElement>(null);
  const isEmpty = lists?.length === 0;
  const isSearchDisabled = !enableSearch;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setQuery('');
        handleSearch('');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleChange = (e: any) => {
    setQuery(e?.target?.value);
    handleSearch(e?.target?.value);
  };
  const refreshData = (e: any) => {
    setQuery('');
    handleSearch('');
  };

  const handleSelected = (value: any) => {
    setSelected(value);
    handleSelectedValue(value);
    setQuery('');
    handleSearch('');

    if (multiSelect) {
      const isAlreadySelected = multiSelected?.some(
        (item) => item?.roleId === value?.roleId,
      );
      if (!isAlreadySelected) {
        !_.isNil(value) && setMultiSelected((prev) => [...prev, value]);
      } else {
        setMultiSelected((prev) =>
          prev.filter((item) => item?.roleId !== value?.roleId),
        );
      }
    }
  };

  const handleMultiSelected = (multiSelectedOptions: List[]) => {
    let multiOptionString = '';
    multiSelectedOptions?.forEach((option, index) => {
      if (option) {
        multiOptionString += option[nameKey];
        if (index < multiSelectedOptions?.length - 1) {
          multiOptionString += ',';
        }
      }
    });
    return { [nameKey]: multiOptionString };
  };

  const multiOptionSelectedValue = multiSelect
    ? handleMultiSelected(multiSelected)
    : { nameKey: '' };

  // When serverSideSearch is true the API already filtered the list — skip
  // client-side re-filtering which can produce incorrect counts/results.
  let filteredList =
    query === '' || serverSideSearch
      ? lists
      : lists?.filter((list) => {
        const searchTerm = query?.toLowerCase()?.replace(/\s+/g, '');
        const searchableKeys = ['name', 'sourceId', 'profile'];

        return searchableKeys.some((key) => {
          const val = list[key];
          if (typeof val === 'string' || typeof val === 'number') {
            return val
              ?.toString()
              ?.toLowerCase()
              ?.replace(/\s+/g, '')
              ?.includes(searchTerm);
          }
          return false;
        });
      });

  if (
    allowCustomValues &&
    query !== '' &&
    !filteredList.some((item) => item[nameKey] === query)
  ) {
    filteredList = [
      ...filteredList,
      { id: `custom-${query}`, [nameKey]: query, isCustom: true },
    ];
  }

  useEffect(() => {
    if (selectedValue) {
      setSelected(selectedValue);
    }
    if (selectedValue && multiSelect) {
      setMultiSelected(selectedValue);
    }
  }, [selectedValue]);

  const basePrimaryOption =
    typeof lists?.[0]?.[nameKey] === 'string' &&
      /^(all|none)\b/i.test(lists[0][nameKey].trim())
      ? lists[0]
      : null;
  const totalCountableOptions = basePrimaryOption ? lists.slice(1) : lists;

  const primaryOption =
    typeof filteredList?.[0]?.[nameKey] === 'string' &&
      /^(all|none)\b/i.test(filteredList[0][nameKey].trim())
      ? filteredList[0]
      : null;
  const availableOptions = primaryOption ? filteredList.slice(1) : filteredList;
  const primaryOptionText =
    typeof primaryOption?.[nameKey] === 'string' &&
      primaryOption[nameKey].toLowerCase().includes('all')
      ? AUTOCOMPLETE_PROPS.PRIMARY_OPTION.USE_ALL
      : AUTOCOMPLETE_PROPS.PRIMARY_OPTION.CLEAR_SELECTION;
  const filteredResultsCount = availableOptions.filter(
    (item) => !item?.isCustom,
  ).length;
  const totalResultsCount = totalCountableOptions.filter(
    (item) => !item?.isCustom,
  ).length;

  return (
    <div className={className || `px-1`} ref={wrapperRef}>
      <Combobox
        value={selected}
        onChange={handleSelected}
        immediate={!isSearchDisabled}
      >
        {({ open }) => (
          <>
            <div className="relative flex">
              {!hideTopLabel && label && (
                <Text
                  as="label"
                  size="md"
                  weight="normal"
                  color="inherit"
                  htmlFor={label}
                  className={cx(
                    `absolute -top-[2px] left-3 ${APP_BG.base} z-10 px-1 leading-none rounded`,
                    {
                      'text-red-500': error,
                      [accentColor]: isFocused && !error,
                      'text-slate-500 dark:text-gray-400': !isFocused,
                    },
                  )}
                >
                  {label}
                </Text>
              )}
            </div>
            <div className="relative my-1 w-full">
              {/* Search icon — only shown when typing is enabled */}
              {!isSearchDisabled && !readOnly && (
                <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center">
                  <MagnifyingGlassIcon
                    className={cx(
                      'h-3.5 w-3.5 transition-colors duration-150',
                      isFocused
                        ? accentColor
                        : 'text-gray-400 dark:text-gray-500',
                    )}
                  />
                </span>
              )}
              <ComboboxInput
                className={cx(
                  // 1. Base styles
                  `block w-full text-sm leading-tight rounded-lg border focus:outline-none truncate text-ellipsis overflow-hidden dark:placeholder-gray-500`,

                  // 2. Search-enabled vs dropdown-only styling
                  isSearchDisabled
                    ? `font-normal ${APP_BG.input} text-gray-700 dark:text-gray-300 ${isFocused && !error ? focusBorderColor : borderColor} cursor-pointer select-none`
                    : `font-normal ${APP_BG.input} text-gray-700 dark:text-gray-300 ${isFocused && !error ? focusBorderColor : borderColor} focus-visible:shadow-orange`,

                  // 3. Conditional Error Styling
                  {
                    'border-red-500': error,
                  },

                  // 4. Padding — extra left pad when search icon is visible
                  isLargePadded
                    ? 'h-11 pr-8 pl-4'
                    : isSearchDisabled || readOnly
                      ? 'h-10 pr-6 pl-2'
                      : 'h-10 pr-6 pl-7',
                )}
                readOnly={readOnly || isSearchDisabled}
                placeholder={placeholder}
                autoComplete="off"
                onClick={
                  isSearchDisabled
                    ? () => comboboxButtonRef.current?.click()
                    : undefined
                }
                style={isSearchDisabled ? { cursor: 'pointer' } : undefined}
                displayValue={(list: any) => {
                  const value = multiSelect
                    ? multiOptionSelectedValue?.[nameKey] || ''
                    : list?.[nameKey] || '';
                  return value
                }}
                onChange={handleChange}
                onFocus={(e) => {
                  setIsFocused(true);
                  if (!isSearchDisabled) {
                    e.target.select();
                  }
                }}
                onBlur={() => {
                  setIsFocused(false);
                  onBlur?.();
                }}
                data-testid={dataTestId}
              />

              {!readOnly && (
                <ComboboxButton
                  ref={comboboxButtonRef}
                  className="absolute inset-y-0 right-0 flex items-center px-2"
                  data-testid={
                    dataTestId ? `${dataTestId}-toggle` : tid('autocomplete-toggle')
                  }
                >
                  <ChevronDownIcon
                    className={cx('h-4 w-4 transition-transform duration-200', {
                      'rotate-180': open,
                      'rotate-0': !open,
                      [accentColor]: isFocused,
                      'text-slate-500 dark:text-gray-400': !isFocused,
                    })}
                    aria-hidden="true"
                  />
                </ComboboxButton>
              )}

              <ComboboxOptions
                anchor="bottom start"
                transition
                className={cx(
                  'z-[9999] mt-1 w-[var(--input-width)]',
                  `focus:outline-none ${APP_BG.base} backdrop-blur-xl`,
                  `border ${APP_BORDER.default} rounded-xl overflow-hidden`,
                  'shadow-[0_8px_32px_-4px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)]',
                  'dark:shadow-[0_8px_32px_-4px_rgba(0,0,0,0.5),0_2px_8px_-2px_rgba(0,0,0,0.3)]',
                  'scroll-class',
                  // Smooth enter / leave
                  'transition duration-150 ease-out',
                  'data-[closed]:opacity-0 data-[closed]:scale-y-95 data-[closed]:-translate-y-1',
                  'origin-top',
                )}
                data-testid={
                  dataTestId ? `${dataTestId}-options` : tid('autocomplete-options')
                }
              >
                <div
                  className={`flex items-center justify-between gap-2 px-3 py-1.5 ${APP_BG.surface} border-b ${APP_BORDER.soft}`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span
                      className={`inline-flex items-center justify-center w-3.5 h-3.5 flex-shrink-0 ${accentColor} opacity-85`}
                    >
                      <MagnifyingGlassIcon className="w-3.5 h-3.5" />
                    </span>
                    <Text
                      as="span"
                      size="2xs"
                      weight="extrabold"
                      color="inherit"
                      uppercase
                      truncate
                      className={`${accentColor} opacity-85`}
                    >
                      {label}
                    </Text>
                  </div>

                  <span
                    className={`inline-flex items-center gap-1 h-[18px] px-2 rounded-full text-[10px] font-bold tabular-nums transition-all duration-250 ${query
                      ? 'bg-amber-100 dark:bg-amber-400/20 text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30'
                      : `${accentBg} ${accentColor} ring-1 ring-current/20`
                      }`}
                  >
                    {query ? (
                      <>
                        <MagnifyingGlassIcon className="w-2.5 h-2.5 opacity-70" />
                        {filteredResultsCount}
                        <span className="opacity-50">/</span>
                        {totalResultsCount}
                      </>
                    ) : (
                      totalResultsCount
                    )}
                  </span>
                </div>

                <div
                  className={`${!height ? 'max-h-60' : `max-h-36`} overflow-y-auto py-1 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700`}
                  onScroll={(e) => {
                    // Guard: skip if no handler, already loading, or not near bottom
                    if (!handleFetchMore || isFetchingMore) return;
                    const el = e.currentTarget;
                    if (Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight - 12) {
                      handleFetchMore();
                    }
                  }}
                >
                  {/* Empty state: no query, no data */}
                  {isEmpty && !query ? (
                    <div className="flex flex-col items-center justify-center py-6 gap-2">
                      <Caption size="base" weight="bold" color="muted">
                        {AUTOCOMPLETE_PROPS.EMPTY.NO_DATA}
                      </Caption>
                    </div>
                  ) : (isEmpty || filteredList?.length === 0) && query !== '' ? (
                    <div className="flex flex-col items-center justify-center py-8 gap-2.5 px-4">
                      <div
                        className={`w-10 h-10 rounded-2xl ${accentBg} flex items-center justify-center`}
                      >
                        <MagnifyingGlassIcon
                          className={`w-5 h-5 ${accentColor} opacity-60`}
                        />
                      </div>
                      <div className="text-center">
                        <Caption size="base" weight="bold" color="muted">
                          {AUTOCOMPLETE_PROPS.EMPTY.NO_RESULTS_TITLE}
                        </Caption>
                        <Caption
                          size="md"
                          color="subtle"
                          className="mt-0.5 block"
                        >
                          {AUTOCOMPLETE_PROPS.EMPTY.NO_RESULTS_SUBTITLE}{' '}
                          <Text
                            as="span"
                            size="md"
                            weight="semibold"
                            color="inherit"
                            className={accentColor}
                          >
                            &ldquo;{query}&rdquo;
                          </Text>
                        </Caption>
                      </div>
                      <AppButton
                        variant="secondary"
                        size="sm"
                        icon={<FiRefreshCcw />}
                        onClick={refreshData}
                        title={AUTOCOMPLETE_PROPS.EMPTY.CLEAR_SEARCH_TITLE}
                        className="mt-1"
                      >
                        {AUTOCOMPLETE_PROPS.EMPTY.CLEAR_SEARCH_LABEL}
                      </AppButton>
                    </div>
                  ) : (
                    <>
                      {primaryOption && (
                        <div className="px-2 pt-1.5 pb-1">
                          <ComboboxOption
                            key={`${primaryOption?.id}-primary`}
                            data-testid={tid(`${label}-dropdown-option-idx-0`)}
                            value={
                              primaryOption.isCustom
                                ? primaryOption[nameKey]
                                : primaryOption
                            }
                          >
                            {({ selected, active }) => (
                              <div
                                className={`relative w-full cursor-pointer text-left px-2.5 py-1.5 text-sm rounded-lg flex items-center justify-between gap-2 border border-dashed transition-all duration-150 ${selected
                                  ? 'shadow-sm'
                                  : active
                                    ? 'border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-700/60'
                                    : 'border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-700/30 hover:bg-gray-200/80 dark:hover:bg-gray-700/50'
                                  }`}
                              >
                                {selected && (
                                  <span
                                    className={`absolute inset-0 rounded-lg bg-gradient-to-r ${selectedGradient}`}
                                    aria-hidden
                                  />
                                )}
                                <div className="flex flex-col min-w-0 relative z-10" data-testid={tid(`${label}-dropdown-option-idx-0-label`)}>
                                  <Text
                                    as="span"
                                    size="sm"
                                    weight="semibold"
                                    color={selected ? 'white' : 'muted'}
                                    truncate
                                    leading="snug"
                                    title={primaryOption[nameKey]}
                                  >
                                    {isCapitalised
                                      ? primaryOption[nameKey]
                                      : firstLetterCapitalise(
                                        primaryOption[nameKey],
                                      )}
                                    {primaryOption.isCustom &&
                                      AUTOCOMPLETE_PROPS.CUSTOM_SUFFIX}
                                  </Text>
                                  <Text
                                    as="span"
                                    size="2xs"
                                    weight="normal"
                                    color={selected ? 'white' : 'muted'}
                                    truncate
                                    leading="tight"
                                  >
                                    {primaryOptionText}
                                  </Text>
                                </div>
                                {selected && (
                                  <span className="relative z-10 inline-flex w-4 h-4 items-center justify-center flex-shrink-0">
                                    <span className="absolute inline-flex h-3 w-3 rounded-full opacity-40 animate-ping bg-white/90" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                                  </span>
                                )}
                              </div>
                            )}
                          </ComboboxOption>
                        </div>
                      )}

                      {availableOptions.length > 0 && (
                        <div className="px-3 pt-1 pb-1">
                          <div
                            className={`h-px w-full bg-current opacity-40 dark:opacity-60 ${accentColor}`}
                          />
                        </div>
                      )}

                      {availableOptions.map((list, idx) => {
                        const showDivider = idx < availableOptions.length - 1;

                        return (
                          <React.Fragment key={`${list?.id}-${idx}`}>
                            <ComboboxOption
                              data-testid={tid(`${label}-dropdown-option-idx-${idx + 1}`)}
                              className={({ active, selected }) =>
                                `relative cursor-pointer select-none px-3 py-2 text-sm flex items-center justify-between gap-2 transition-all duration-150 ${selected
                                  ? 'shadow-sm'
                                  : active
                                    ? 'bg-gray-100/80 dark:bg-gray-700/60'
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700/50'
                                }`
                              }
                              value={list.isCustom ? list[nameKey] : list}
                            >
                              {({ selected }) => (
                                <>
                                  {selected && (
                                    <span
                                      className={`absolute inset-0 bg-gradient-to-r ${selectedGradient}`}
                                      aria-hidden
                                    />
                                  )}
                                  <Text
                                    as="span"
                                    size="lg"
                                    color={selected ? 'white' : 'default'}
                                    truncate
                                    leading="snug"
                                    className="relative z-10 block"
                                    title={list[nameKey]}
                                    data-tip
                                    data-for={`option-tooltip-${list.id}`}
                                  >
                                    {isCapitalised
                                      ? list[nameKey]
                                      : firstLetterCapitalise(list[nameKey])}
                                    {list.isCustom &&
                                      AUTOCOMPLETE_PROPS.CUSTOM_SUFFIX}
                                  </Text>
                                  <Tooltip
                                    id={`option-tooltip-${list.id}`}
                                    place="top"
                                  >
                                    {firstLetterCapitalise(list[nameKey])}
                                  </Tooltip>
                                  {selected && (
                                    <span className="relative z-10 inline-flex w-4 h-4 items-center justify-center flex-shrink-0">
                                      <span className="absolute inline-flex h-3 w-3 rounded-full opacity-40 animate-ping bg-white/90" />
                                      <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                                    </span>
                                  )}
                                </>
                              )}
                            </ComboboxOption>
                            {showDivider && (
                              <div
                                className={`w-full h-px ${APP_BORDER.dividerBg}`}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                  {/* Fetch-more loading indicator */}
                  {isFetchingMore && (
                    <div className={`flex items-center justify-center gap-1.5 py-2 ${accentColor} opacity-60`}>
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
                      <span className="text-[11px] font-medium">Loading more…</span>
                    </div>
                  )}
                </div>
              </ComboboxOptions>
            </div>
          </>
        )}
      </Combobox>
    </div>
  );
};

export default Autocomplete;
